import React, { useEffect, useState } from 'react';
import { Package, User as UserIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { ordersAPI, authAPI } from '@/utils/api';
import { toast } from 'sonner';

const UserDashboard = () => {
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [userRes, ordersRes] = await Promise.all([
        authAPI.getMe(),
        ordersAPI.getAll()
      ]);
      setUser(userRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="user-dashboard">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl sm:text-5xl font-heading font-black tracking-tight mb-8" data-testid="dashboard-title">
            My Dashboard
          </h1>
          
          {/* User Info */}
          <div className="mb-8 p-6 rounded-2xl border border-white/10 bg-white/5" data-testid="user-info">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full rounded-full" />
                ) : (
                  <UserIcon className="w-8 h-8 text-white" />
                )}
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold" data-testid="user-name">{user?.name}</h2>
                <p className="text-muted-foreground" data-testid="user-email">{user?.email}</p>
              </div>
            </div>
          </div>
          
          {/* Orders */}
          <div>
            <h2 className="text-2xl font-heading font-bold mb-6">My Orders</h2>
            
            {orders.length === 0 ? (
              <div className="text-center py-16 p-6 rounded-2xl border border-white/10 bg-white/5" data-testid="no-orders-message">
                <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-heading font-bold mb-2">No orders yet</h3>
                <p className="text-muted-foreground">Start shopping to see your orders here</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="orders-list">
                {orders.map((order) => (
                  <div
                    key={order.order_id}
                    className="p-6 rounded-2xl border border-white/10 bg-white/5"
                    data-testid={`order-${order.order_id}`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-heading font-bold text-lg mb-1" data-testid={`order-id-${order.order_id}`}>
                          Order #{order.order_id}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {new Date(order.created_at).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold ${
                          order.status === 'completed'
                            ? 'bg-accent/10 text-accent'
                            : 'bg-muted text-muted-foreground'
                        }`}
                        data-testid={`order-status-${order.order_id}`}
                      >
                        {order.status.toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      {order.items.map((item, index) => (
                        <div key={index} className="flex justify-between text-sm" data-testid={`order-item-${order.order_id}-${index}`}>
                          <span>{item.title} (x{item.quantity})</span>
                          <span className="font-heading font-bold">₹{item.price * item.quantity}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="border-t border-white/10 pt-4 flex justify-between items-center">
                      <span className="text-muted-foreground">Total Amount</span>
                      <span className="text-xl font-heading font-black" data-testid={`order-total-${order.order_id}`}>
                        ₹{order.total_amount}
                      </span>
                    </div>
                    
                    {order.status === 'completed' && (
                      <p className="text-xs text-muted-foreground mt-4">
                        Delivered to: {order.delivery_email}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default UserDashboard;
